import { useMemo, useRef, useState } from "react";
import {
  createPigmentClient,
  pigmentAuthoringPropertyOptions,
  pigmentAuthoringValueSource,
  type PigmentAuthoringSnapshot,
  type PigmentAuthoringBlockContext,
  type PigmentAuthoringPropertyOption,
  type PigmentCommittedMutation,
  type PigmentInspectionAcceptanceReceipt,
  type PigmentInspectionFailure,
  type PigmentInspectionReportRead,
  type PigmentClient,
  type PigmentPreparedMutation,
  type PigmentPublished,
  type PigmentSiteContent,
} from "@pigment/client";
import type {
  PigmentCapabilityManifest,
  PigmentDocumentReconcileResult,
} from "@pigment/core";
import {
  EMPTY_OPERATION,
  applyGuidedOperation,
  describeMutationDiff,
  desiredAuthoringContext,
  guidedPlacementOptions,
  presentRefusal,
  presentReconciliationRefusal,
  normalizePigmentHostOrigin,
  selectAuthoringBlock,
  selectAuthoringProperty,
  selectedAuthoringBlock,
  type OperationDraft,
  type RefusalPresentation,
} from "./domain.js";
import {
  commitDesiredAuthoring,
  desiredDocumentEdited,
  prepareDesiredAuthoring,
  publishDesiredAuthoring,
  readDesiredAuthoring,
  reconcileDesiredAuthoring,
} from "./authoring-workflow.js";
import type { PigmentBrowserInspectionJob } from "@pigment/browser-inspector";
import { summarizeEvidence } from "./evidence.js";

type BusyAction = "connect" | "read" | "reconcile" | "prepare" | "inspect" | "commit" | "publish" | null;

const STALE_CONSOLE_CONTEXT = Symbol("stale-console-context");

function initialHost(): string {
  return import.meta.env.VITE_PIGMENT_HOST_ORIGIN || "https://alpha.owlandkestrel.com";
}

export function App() {
  const [host, setHost] = useState(initialHost);
  const [client, setClient] = useState<PigmentClient | null>(null);
  const [page, setPage] = useState("home");
  const [manifest, setManifest] = useState<PigmentCapabilityManifest | null>(null);
  const [snapshot, setSnapshot] = useState<PigmentAuthoringSnapshot | null>(null);
  const [content, setContent] = useState<PigmentSiteContent | null>(null);
  const [desiredSource, setDesiredSource] = useState("");
  const [reconciliation, setReconciliation] = useState<PigmentDocumentReconcileResult | null>(null);
  const [operationDraft, setOperationDraft] = useState<OperationDraft>(EMPTY_OPERATION);
  const [prepared, setPrepared] = useState<PigmentPreparedMutation | null>(null);
  const [inspectionJob, setInspectionJob] = useState<PigmentBrowserInspectionJob | null>(null);
  const [inspectionAcceptance, setInspectionAcceptance] = useState<PigmentInspectionAcceptanceReceipt | null>(null);
  const [inspectionFailure, setInspectionFailure] = useState<PigmentInspectionFailure | null>(null);
  const [inspectionReport, setInspectionReport] = useState<PigmentInspectionReportRead | null>(null);
  const [committed, setCommitted] = useState<PigmentCommittedMutation | null>(null);
  const [published, setPublished] = useState<PigmentPublished | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<RefusalPresentation | null>(null);
  const [busy, setBusy] = useState<BusyAction>(null);
  // Host, page, and desired-document bytes are authority-bearing inputs. Async
  // completions from an earlier generation must never reinstall stale lineage.
  const authorityGeneration = useRef(0);

  const authoringContext = useMemo(() => desiredAuthoringContext(snapshot, desiredSource), [snapshot, desiredSource]);
  const mutation = reconciliation?.ok ? reconciliation.mutation : null;
  const selectedBlock = useMemo(() => selectedAuthoringBlock(authoringContext, operationDraft.id), [authoringContext, operationDraft.id]);
  const propertyOptions = useMemo(() => pigmentAuthoringPropertyOptions(selectedBlock), [selectedBlock]);
  const selectedProperty = propertyOptions.find((property) => property.key === operationDraft.key) ?? null;
  const placementOptions = useMemo(
    () => guidedPlacementOptions(authoringContext, desiredSource, operationDraft),
    [authoringContext, desiredSource, operationDraft],
  );

  function clearLineage() {
    setPrepared(null);
    setInspectionJob(null);
    setInspectionAcceptance(null);
    setInspectionFailure(null);
    setInspectionReport(null);
    setCommitted(null);
    setPublished(null);
  }

  function invalidateCompilation(nextDesiredSource: string) {
    authorityGeneration.current += 1;
    const next = desiredDocumentEdited(nextDesiredSource);
    setDesiredSource(next.desiredSource);
    setReconciliation(next.reconciliation);
    setPrepared(next.prepared);
    setInspectionJob(next.inspectionJob);
    setInspectionAcceptance(next.inspectionAcceptance);
    setInspectionFailure(next.inspectionFailure);
    setInspectionReport(next.inspectionReport);
    setCommitted(next.committed);
    setPublished(next.published);
  }

  function clearPage() {
    setSnapshot(null);
    setContent(null);
    setOperationDraft(EMPTY_OPERATION);
    invalidateCompilation("");
  }

  function requireConnectedClient(): PigmentClient {
    if (!client) throw new Error("Connect to a Pigment host before using its authoring surface.");
    return client;
  }

  function applyGuidedEdit() {
    try {
      if (!authoringContext) throw new Error("Read a complete desired document before applying a guided edit.");
      const nextSource = applyGuidedOperation(desiredSource, operationDraft, authoringContext, placementOptions);
      invalidateCompilation(nextSource);
      const nextContext = desiredAuthoringContext(snapshot, nextSource);
      setOperationDraft(operationDraft.op === "insert"
        ? { ...EMPTY_OPERATION, op: operationDraft.op, blockType: operationDraft.blockType }
        : selectAuthoringBlock(operationDraft, nextContext, operationDraft.id));
      setRefusal(null);
    } catch (error) {
      setRefusal(presentRefusal(error));
    }
  }

  function chooseOperation(op: OperationDraft["op"]) {
    const next = {
      ...operationDraft,
      op,
      placementId: "",
      ...(op === "insert" ? {
        id: "",
        key: "",
        value: "",
        blockType: operationDraft.blockType || authoringContext?.availableBlocks[0]?.type || "",
        exampleIndex: 0,
      } : {}),
    };
    if (op === "insert" || next.id || !authoringContext?.blocks[0]) {
      setOperationDraft(next);
      return;
    }
    setOperationDraft(selectAuthoringBlock(next, authoringContext, authoringContext.blocks[0].id));
  }

  async function perform(
    action: Exclude<BusyAction, null>,
    work: (assertCurrent: () => void) => Promise<void>,
  ) {
    const generation = authorityGeneration.current;
    const assertCurrent = () => {
      if (authorityGeneration.current !== generation) throw STALE_CONSOLE_CONTEXT;
    };
    setBusy(action);
    setRefusal(null);
    setNotice(null);
    try {
      await work(assertCurrent);
    } catch (error) {
      if (error !== STALE_CONSOLE_CONTEXT) setRefusal(presentRefusal(error));
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="shell">
      <header className="masthead">
        <a className="brand" href="#top" aria-label="Pigment console home">
          <span className="brand-mark" aria-hidden="true">P</span>
          <span><strong>Pigment</strong><small>semantic authoring console</small></span>
        </a>
        <div className="session-policy"><i /> Delegated session only</div>
      </header>

      <section className="intro" id="top">
        <div>
          <p className="kicker">A deliberate path from intent to publication</p>
          <h1>Shape the page.<br /><em>Keep the lineage.</em></h1>
        </div>
        <p className="lede">Connect to a Pigment-capable host, compose semantic edits, inspect an immutable preview, then commit and publish as separate acts of authority.</p>
      </section>

      <section className="connection panel" aria-labelledby="connection-title">
        <div className="section-number">01</div>
        <div className="section-heading">
          <p className="eyebrow">Host</p>
          <h2 id="connection-title">Connect and discover</h2>
          <p>Uses the browser’s existing delegated-user session. No tokens enter this surface.</p>
        </div>
        <div className="connection-form">
          <Field label="Host origin">
            <input value={host} disabled={busy !== null && busy !== "read"} onChange={(event) => {
              setHost(event.target.value);
              setClient(null);
              setManifest(null);
              clearPage();
            }} inputMode="url" spellCheck={false} />
          </Field>
          <button className="button primary" disabled={busy !== null} onClick={() => perform("connect", async (assertCurrent) => {
            const origin = normalizePigmentHostOrigin(host);
            const candidateClient = createPigmentClient({
              baseUrl: origin,
              credentials: "include",
            });
            const discovered = await candidateClient.discover();
            assertCurrent();
            setHost(origin);
            setClient(candidateClient);
            setManifest(discovered);
            clearPage();
          })}>{busy === "connect" ? "Discovering…" : "Connect"}</button>
        </div>
        <div className={`connection-state ${manifest ? "is-ready" : ""}`}>
          <span>{manifest ? "Connected" : "Not connected"}</span>
          <strong>{manifest ? `${manifest.blocks.length} blocks · manifest ${manifest.version}` : "Discovery verifies the host contract"}</strong>
        </div>
      </section>

      <div className="workspace-grid">
        <section className="panel document-panel" aria-labelledby="document-title">
          <div className="section-number">02</div>
          <div className="section-heading compact">
            <p className="eyebrow">Authority state</p>
            <h2 id="document-title">Choose a page</h2>
          </div>
          <div className="page-row">
            <input aria-label="Page slug" value={page} disabled={busy !== null && busy !== "read"} onChange={(event) => {
              setPage(event.target.value);
              clearPage();
            }} spellCheck={false} />
            <button className="button secondary" disabled={!manifest || busy !== null || !page.trim()} onClick={() => perform("read", async (assertCurrent) => {
              const next = await readDesiredAuthoring(requireConnectedClient(), page);
              assertCurrent();
              setSnapshot(next.snapshot);
              setManifest(next.snapshot.context.manifest);
              setContent(next.snapshot.content);
              invalidateCompilation(next.desiredSource);
              setOperationDraft(next.snapshot.context.blocks[0]
                ? selectAuthoringBlock({ ...EMPTY_OPERATION, blockType: next.snapshot.context.availableBlocks[0]?.type ?? "" }, next.snapshot.context, next.snapshot.context.blocks[0].id)
                : { ...EMPTY_OPERATION, blockType: next.snapshot.context.availableBlocks[0]?.type ?? "" });
            })}>{busy === "read" ? "Reading…" : "Read page"}</button>
          </div>
          <div className="revision-card">
            <span>Draft revision</span>
            <strong>{content ? `r${content.draftRevision}` : "—"}</strong>
            <small>{snapshot?.authoringBase
              ? `${snapshot.authoringBase.source} base · ${snapshot.authoringBase.provenance}`
              : content?.published ? "Published document exists" : "No verified authoring base"}</small>
          </div>
          {authoringContext && <div className="authoring-summary" aria-live="polite">
            <strong>{authoringContext.blocks.length} authored block{authoringContext.blocks.length === 1 ? "" : "s"}</strong>
            <span>{authoringContext.availableBlocks.length} host capabilities · {authoringContext.diagnostics.length} diagnostic{authoringContext.diagnostics.length === 1 ? "" : "s"}</span>
          </div>}
          <details className="json-disclosure">
            <summary>Inspect current authority JSON</summary>
            <pre>{content ? JSON.stringify(content, null, 2) : "Read a page to inspect its draft and published state."}</pre>
          </details>
        </section>

        <section className="panel composer-panel" aria-labelledby="composer-title">
          <div className="section-number">03</div>
          <div className="section-heading compact">
            <p className="eyebrow">Desired semantic state</p>
            <h2 id="composer-title">Describe the page you want</h2>
          </div>
          <div className="desired-editor">
            <p>Guided edits update the complete local desired document immediately. Pigment will later compile the smallest deterministic mutation; there is no operation queue to order or mutation JSON to author.</p>
            <div className="operation-form">
              <Field label="Edit">
                <select value={operationDraft.op} disabled={!authoringContext} onChange={(event) => chooseOperation(event.target.value as OperationDraft["op"])}>
                  <option value="set">Set property</option>
                  <option value="reset">Reset property</option>
                  <option value="insert">Insert block</option>
                  <option value="move">Move block</option>
                  <option value="remove">Remove block</option>
                </select>
              </Field>
              {operationDraft.op === "insert" ? <>
                <Field label="Block type"><select value={operationDraft.blockType} onChange={(event) => setOperationDraft({ ...operationDraft, blockType: event.target.value, exampleIndex: 0, placementId: "" })}>
                  <option value="">Select a host capability…</option>
                  {authoringContext?.availableBlocks.map((capability) => <option key={capability.type} value={capability.type}>{capability.label ?? capability.type}{capability.deprecated ? " · deprecated" : ""}</option>)}
                </select></Field>
                {(authoringContext?.availableBlocks.find((capability) => capability.type === operationDraft.blockType)?.examples?.length ?? 0) > 1 && <Field label="Example"><select value={operationDraft.exampleIndex} onChange={(event) => setOperationDraft({ ...operationDraft, exampleIndex: Number(event.target.value) })}>
                  {authoringContext!.availableBlocks.find((capability) => capability.type === operationDraft.blockType)!.examples!.map((_, index) => <option key={index} value={index}>Example {index + 1}</option>)}
                </select></Field>}
                <Field label="Stable block ID"><input value={operationDraft.id} onChange={(event) => setOperationDraft({ ...operationDraft, id: event.target.value })} placeholder="hero-summary" /></Field>
              </> : <Field label="Block">
                <select value={operationDraft.id} disabled={!authoringContext?.blocks.length} onChange={(event) => setOperationDraft(selectAuthoringBlock(operationDraft, authoringContext, event.target.value))}>
                  <option value="">Select a block…</option>
                  {authoringContext?.blocks.map((block) => <option key={block.id} value={block.id}>{block.capability?.label ?? block.type} · {block.id} · {block.path}{block.known ? "" : " · unknown"}</option>)}
                </select>
              </Field>}
              {(operationDraft.op === "set" || operationDraft.op === "reset") && <Field label="Property">
                <select value={operationDraft.key} disabled={!selectedBlock || propertyOptions.length === 0} onChange={(event) => setOperationDraft(selectAuthoringProperty(operationDraft, authoringContext, event.target.value))}>
                  <option value="">Select a property…</option>
                  {propertyOptions.map((property) => <option key={property.key} value={property.key}>{property.key}{property.schema ? ` · ${property.schema.kind}` : " · undeclared"}{property.hasCurrent ? " · authored" : ""}</option>)}
                </select>
              </Field>}
              {operationDraft.op === "set" && selectedProperty && <PropertyValueControl property={selectedProperty} value={operationDraft.value} onChange={(value) => setOperationDraft({ ...operationDraft, value })} />}
              {(operationDraft.op === "set" || operationDraft.op === "reset") && selectedBlock && selectedProperty && <PropertyGuide block={selectedBlock} property={selectedProperty} />}
              {(operationDraft.op === "insert" || operationDraft.op === "move") && <Field label="Placement"><select value={operationDraft.placementId} disabled={!placementOptions.length} onChange={(event) => setOperationDraft({ ...operationDraft, placementId: event.target.value })}>
                <option value="">{placementOptions.length ? "Choose a legal destination…" : "No legal destination"}</option>
                {placementOptions.map((placement) => {
                  const selectionKey = placement.selectionKey ?? placement.id;
                  return <option key={selectionKey} value={selectionKey}>{placement.label}</option>;
                })}
              </select></Field>}
              <button className="button tertiary" disabled={!authoringContext || busy !== null || Boolean(committed) || ((operationDraft.op === "insert" || operationDraft.op === "move") && !operationDraft.placementId)} onClick={applyGuidedEdit}>Apply to desired state</button>
            </div>
            <details className="raw-editor">
              <summary>Advanced · edit complete desired-document JSON</summary>
              <textarea rows={22} value={desiredSource} disabled={!snapshot?.authoringBase || busy !== null} onChange={(event) => invalidateCompilation(event.target.value)} spellCheck={false} />
            </details>
            <button className="button secondary" disabled={!snapshot?.authoringBase || !desiredSource.trim() || busy !== null || Boolean(committed)} onClick={() => perform("reconcile", async (assertCurrent) => {
              const next = await reconcileDesiredAuthoring(requireConnectedClient(), snapshot!, desiredSource);
              assertCurrent();
              setReconciliation(next);
              clearLineage();
              if (!next.ok) setRefusal(presentReconciliationRefusal(next));
            })}>{busy === "reconcile" ? "Reconciling…" : "Reconcile desired state"}</button>
          </div>
          {reconciliation?.ok && <div className="operation-stack" aria-live="polite">
            <div className="stack-heading"><span>{reconciliation.mutation.operations.length} compiled operation{reconciliation.mutation.operations.length === 1 ? "" : "s"}</span><code>r{reconciliation.mutation.expectedRevision}</code></div>
            {reconciliation.mutation.operations.length === 0
              ? <p className="empty">The desired document already matches the verified host base. No preview or write is needed.</p>
              : reconciliation.mutation.operations.map((operation, index) => <div className="operation" key={`${operation.op}-${index}`}>
                <code>{operation.op}</code><span>{"id" in operation ? operation.id : operation.op === "insert" ? operation.block.id : operation.op === "set-chrome-mode" ? `chrome.${operation.slot}` : "theme"}</span>
              </div>)}
          </div>}
          {reconciliation?.ok && <details className="json-disclosure"><summary>Inspect compiled mutation and predicted diff</summary><pre>{JSON.stringify(reconciliation, null, 2)}</pre></details>}
        </section>
      </div>

      <section className="panel workflow" aria-labelledby="workflow-title">
        <div className="section-number">04</div>
        <div className="section-heading">
          <p className="eyebrow">Lineage</p>
          <h2 id="workflow-title">Prove, then act</h2>
          <p>Planning and previewing are write-free. Browser inspection, commit, and publish are distinct gates.</p>
        </div>
        <div className="workflow-steps">
          <WorkflowStep number="A" title="Plan + preview" state={prepared ? "complete" : mutation && mutation.operations.length > 0 ? "ready" : "locked"} detail={prepared ? `r${prepared.plan.baseRevision} → r${prepared.plan.candidateRevision} · ${prepared.plan.diff.length} diffs` : "Unlocks after desired state compiles to an effective semantic mutation."}>
            <button className="button primary" disabled={!reconciliation?.ok || !mutation || mutation.operations.length === 0 || busy !== null || Boolean(committed)} onClick={() => perform("prepare", async (assertCurrent) => {
              if (!reconciliation?.ok) throw new Error("Reconcile desired state before preparing a preview.");
              const next = await prepareDesiredAuthoring(requireConnectedClient(), page, reconciliation);
              assertCurrent();
              setPrepared(next);
              setInspectionJob(null);
              setInspectionAcceptance(null);
              setInspectionFailure(null);
              setInspectionReport(null);
              setCommitted(null);
              setPublished(null);
            })}>{busy === "prepare" ? "Preparing…" : "Plan + preview"}</button>
          </WorkflowStep>
          <WorkflowStep number="B" title="Browser inspection" state={inspectionFailure ? "failed" : inspectionAcceptance ? "complete" : prepared ? "ready" : "locked"} detail={inspectionFailure ? `${inspectionFailure.code}: ${inspectionFailure.message}` : inspectionAcceptance ? `Host acceptance ${inspectionAcceptance.body.receiptId}` : inspectionJob ? `Executor job ${inspectionJob.jobId} is pending` : "Request a host job, then await its trusted executor. Author metadata is never acceptance."}>
            {inspectionFailure ? <span className="text-link">Edit desired state, reconcile, then create a fresh plan.</span> : <button className="button secondary" disabled={!prepared || busy !== null || Boolean(committed)} onClick={() => perform("inspect", async (assertCurrent) => {
              const activeClient = requireConnectedClient();
              const job = inspectionJob ?? await activeClient.requestInspectionJob(prepared!);
              assertCurrent();
              setInspectionJob(job);
              if (typeof window !== "undefined") window.open(job.previewUrl, "_blank", "noopener,noreferrer");
              const terminal = await activeClient.awaitInspectionTerminal(prepared!, job);
              assertCurrent();
              if (terminal.status === "accepted") {
                const report = await activeClient.readInspectionReport(prepared!, job, terminal.acceptance);
                assertCurrent();
                setInspectionAcceptance(terminal.acceptance);
                setInspectionFailure(null);
                setInspectionReport(report);
                return;
              }
              const report = await activeClient.readInspectionReport(prepared!, job, terminal.failure);
              assertCurrent();
              setInspectionAcceptance(null);
              setInspectionFailure(terminal.failure);
              setInspectionReport(report);
            })}>{busy === "inspect" ? (inspectionJob ? "Awaiting executor…" : "Requesting…") : inspectionJob ? "Await acceptance" : "Request inspection"}</button>}
            {prepared && <a className="text-link" href={prepared.preview.previewUrl} target="_blank" rel="noreferrer">Open preview ↗</a>}
          </WorkflowStep>
          <WorkflowStep number="C" title="Commit draft" state={committed ? "complete" : inspectionAcceptance && inspectionReport ? "ready" : "locked"} detail={committed ? `Committed r${committed.commit.revision}` : "Unlocks only after the accepted browser report is fetched and revalidated in this client."}>
            <button className="button commit" disabled={!prepared || !inspectionAcceptance || !inspectionReport || busy !== null || Boolean(committed)} onClick={() => perform("commit", async (assertCurrent) => {
              const activeClient = requireConnectedClient();
              const next = await commitDesiredAuthoring(activeClient, prepared!, inspectionAcceptance!);
              assertCurrent();
              setCommitted(next);
              const nextContent = await activeClient.read(page);
              assertCurrent();
              setContent(nextContent);
            })}>{busy === "commit" ? "Committing…" : "Commit draft"}</button>
          </WorkflowStep>
          <WorkflowStep number="D" title="Publish" state={published ? "complete" : committed ? "ready" : "locked"} detail={published ? "Publication receipt accepted" : "Publishes the committed revision and candidate hash receipt."}>
            <button className="button publish" disabled={!committed || busy !== null || Boolean(published)} onClick={() => perform("publish", async (assertCurrent) => {
              const activeClient = requireConnectedClient();
              const nextPublished = await publishDesiredAuthoring(activeClient, committed!);
              assertCurrent();
              setPublished(nextPublished);
              const nextContent = await activeClient.read(page);
              assertCurrent();
              setContent(nextContent);
            })}>{busy === "publish" ? "Publishing…" : "Publish page"}</button>
          </WorkflowStep>
        </div>
        {prepared && <section className="plan-diff" aria-labelledby="plan-diff-title">
          <div className="plan-diff-heading">
            <div><p className="eyebrow">Host-computed semantic diff</p><h3 id="plan-diff-title">What this candidate changes</h3></div>
            <span>r{prepared.plan.baseRevision} → r{prepared.plan.candidateRevision}</span>
          </div>
          {prepared.plan.diff.length === 0 ? <p className="empty">The host reported no effective document changes.</p> : <ol>
            {prepared.plan.diff.map((diff, index) => <li key={`${diff.op}-${"id" in diff ? diff.id : index}-${index}`}>
              <strong>{describeMutationDiff(diff)}</strong>
              <code>{JSON.stringify(diff)}</code>
            </li>)}
          </ol>}
        </section>}
        {inspectionReport && <EvidencePanel evidence={inspectionReport} acceptance={inspectionAcceptance} failure={inspectionFailure} />}
        {prepared && <details className="json-disclosure lineage"><summary>Inspect prepared lineage</summary><pre>{JSON.stringify({ plan: prepared.plan, preview: prepared.preview.artifact, inspectionJob, inspectionAcceptance, inspectionFailure, inspectionReportHash: inspectionReport?.reportHash, receipt: committed?.receipt, published }, null, 2)}</pre></details>}
      </section>

      {(notice || refusal) && (
        <aside className={`message ${refusal ? "refusal" : "notice"}`} role="alert">
          <div>
            <p className="eyebrow">{refusal?.eyebrow ?? "Inspection boundary"}</p>
            <h2>{refusal?.title ?? "Preview opened; acceptance remains pending"}</h2>
            <p>{refusal?.detail ?? notice}</p>
            {refusal && <strong>{refusal.recovery}</strong>}
          </div>
          <button aria-label="Dismiss message" onClick={() => { setNotice(null); setRefusal(null); }}>×</button>
          {Boolean(refusal?.body) && <details><summary>Typed response body</summary><pre>{JSON.stringify(refusal?.body, null, 2)}</pre></details>}
        </aside>
      )}

      <footer>
        <span>Pigment Console · P6 foundation</span>
        <span>Plan ≠ commit · Preview metadata ≠ browser evidence</span>
      </footer>
    </main>
  );
}

function EvidencePanel({ evidence, acceptance, failure }: {
  evidence: PigmentInspectionReportRead;
  acceptance: PigmentInspectionAcceptanceReceipt | null;
  failure: PigmentInspectionFailure | null;
}) {
  const browserReport = evidence.report.schema === "pigment.browser-inspection-report.v2" ? evidence.report : null;
  const summary = browserReport ? summarizeEvidence(browserReport) : null;
  const metrics = [
    ["Viewports", summary?.viewports ?? 0],
    ["Axe violations", summary?.axeViolations ?? 0],
    ["Axe incomplete", summary?.axeIncomplete ?? 0],
    ["Console errors", summary?.consoleErrors ?? 0],
    ["Network failures", summary?.networkFailures ?? 0],
    ["Overflow", summary?.overflowViewports ?? 0],
    ["Unmapped blocks", summary?.unmappedBlocks ?? 0],
  ] as const;

  return <section className="evidence-panel" aria-labelledby="evidence-title">
    <div className="evidence-heading">
      <div>
        <p className="eyebrow">{failure ? "Terminal inspection evidence" : "Accepted browser truth"}</p>
        <h2 id="evidence-title">{failure ? "What stopped publication" : "The pixels behind the receipt"}</h2>
      </div>
      <span className={`evidence-status${failure ? " is-failed" : ""}`}>{failure ? "Failed · exact report verified" : "Passed · exact report verified"}</span>
    </div>
    {failure && <p className="evidence-failure"><strong>{failure.code}</strong> · {failure.message} Edit desired state, reconcile, then create a fresh plan and inspection; this terminal job cannot unlock commit.</p>}
    {browserReport ? <>
      <div className="evidence-metrics" aria-label="Browser evidence summary">
        {metrics.map(([label, value]) => <div key={label} className={value === 0 ? "is-green" : "is-attention"}><span>{label}</span><strong>{value}</strong></div>)}
      </div>
      <div className="evidence-grid">
      {browserReport.evidence.map((viewport) => <article className="evidence-card" key={viewport.viewport.name}>
        <div className="evidence-card-heading">
          <div><p className="eyebrow">{viewport.viewport.name}</p><h3>{viewport.viewport.width} × {viewport.viewport.height}</h3></div>
          <span>{viewport.blocks.filter((block) => block.status === "mapped").length}/{viewport.blocks.length} blocks</span>
        </div>
        <a href={viewport.screenshot.uri} target="_blank" rel="noreferrer" title={`Open ${viewport.viewport.name} evidence PNG`}>
          <img src={viewport.screenshot.uri} alt={`${viewport.viewport.name} accepted preview at ${viewport.viewport.width} by ${viewport.viewport.height} pixels`} />
        </a>
        <dl>
          <div><dt>Axe</dt><dd>{viewport.axe.violations.length} violations</dd></div>
          <div><dt>Console</dt><dd>{viewport.console.filter((entry) => entry.level === "error").length} errors</dd></div>
          <div><dt>Network</dt><dd>{viewport.network.filter((entry) => Boolean(entry.failure) || (typeof entry.status === "number" && entry.status >= 400)).length} failures</dd></div>
          <div><dt>Overflow</dt><dd>{viewport.horizontalOverflow.detected ? "Detected" : "Clear"}</dd></div>
        </dl>
      </article>)}
      </div>
    </> : <p className="evidence-failure">The executor exhausted its attempts before a browser report could be captured. There are no screenshots to inspect; create a fresh plan and inspection after the executor path is healthy.</p>}
    <details className="json-disclosure evidence-lineage">
      <summary>Verify report and terminal hashes</summary>
      <pre>{JSON.stringify({
        reportId: evidence.report.reportId,
        reportHash: evidence.reportHash,
        ...(acceptance ? {
          receiptId: acceptance.body.receiptId,
          receiptHash: acceptance.receiptHash,
          preview: acceptance.body.binding.preview,
          acceptedAt: acceptance.body.acceptedAt,
          acceptanceExpiresAt: acceptance.body.expiresAt,
        } : {}),
        ...(failure ? { failure } : {}),
        ...(browserReport ? { executor: browserReport.executor } : { executorExhaustion: evidence.report }),
      }, null, 2)}</pre>
    </details>
  </section>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label><span>{label}</span>{children}</label>;
}

function PropertyValueControl({ property, value, onChange }: {
  property: PigmentAuthoringPropertyOption;
  value: string;
  onChange(value: string): void;
}) {
  const schema = property.schema;
  if (schema?.enum) {
    return <Field label="New value"><select value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">Choose a declared value…</option>
      {schema.enum.map((option) => {
        const source = pigmentAuthoringValueSource(option, schema);
        return <option key={exactJson(option)} value={source}>{typeof option === "string" ? option : exactJson(option)}</option>;
      })}
    </select></Field>;
  }
  if (schema?.kind === "boolean") {
    return <Field label="New value"><select value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">Choose true or false…</option>
      <option value="true">true</option>
      <option value="false">false</option>
    </select></Field>;
  }
  if (schema?.kind === "number") {
    return <Field label="New value"><input type="number" value={value} onChange={(event) => onChange(event.target.value)} /></Field>;
  }
  if (schema?.kind === "richText") {
    return <Field label="New value"><textarea rows={4} value={value} onChange={(event) => onChange(event.target.value)} /></Field>;
  }
  if (schema?.kind === "array" || schema?.kind === "object" || schema?.kind === "json" || schema === null) {
    return <Field label="New value · JSON"><textarea rows={5} value={value} onChange={(event) => onChange(event.target.value)} spellCheck={false} /></Field>;
  }
  return <Field label="New value"><input value={value} onChange={(event) => onChange(event.target.value)} spellCheck={schema?.kind === "url" ? false : undefined} /></Field>;
}

function PropertyGuide({ block, property }: { block: PigmentAuthoringBlockContext; property: PigmentAuthoringPropertyOption }) {
  const schema = property.schema;
  return <aside className="property-guide wide" aria-label={`Authoring context for ${block.id}.${property.key}`}>
    <div className="property-guide-heading">
      <div><p className="eyebrow">Desired-state context</p><strong>{block.capability?.label ?? block.type} · {block.id}.{property.key}</strong></div>
      <code>{block.path}{block.slot ? ` · slot ${block.slot}` : ""}</code>
    </div>
    <dl>
      <div><dt>Desired value</dt><dd><code>{property.hasCurrent ? exactJson(property.current) : "unset"}</code></dd></div>
      <div><dt>Host default</dt><dd><code>{property.hasDefault ? exactJson(property.defaultValue) : "none declared"}</code></dd></div>
      <div><dt>Schema</dt><dd>{schema ? `${schema.kind}${schema.required ? " · required" : ""}` : "Undeclared legacy property"}</dd></div>
      <div><dt>Choices</dt><dd>{schema?.enum ? schema.enum.map(exactJson).join(" · ") : "Any schema-valid JSON value"}</dd></div>
    </dl>
    {schema?.description && <p>{schema.description}</p>}
    {schema?.deprecated && <p className="property-warning"><strong>Deprecated:</strong> {schema.deprecated.message}{schema.deprecated.replacement ? ` Use ${schema.deprecated.replacement}.` : ""}</p>}
    {!block.known && <p className="property-warning"><strong>Unknown block:</strong> it remains visible for explicit repair, but the host declares no capability for this type.</p>}
  </aside>;
}

function exactJson(value: unknown): string {
  return JSON.stringify(value) ?? "undefined";
}

function WorkflowStep({ number, title, state, detail, children }: { number: string; title: string; state: "ready" | "locked" | "complete" | "failed"; detail: string; children: React.ReactNode }) {
  return <article className={`workflow-step is-${state}`}>
    <div className="step-marker">{state === "complete" ? "✓" : number}</div>
    <div className="step-copy"><h3>{title}</h3><p>{detail}</p></div>
    <div className="step-actions">{children}</div>
  </article>;
}
